import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { BoxPlotController, BoxAndWiskers } from '@sgratzl/chartjs-chart-boxplot';
import { PLATFORM_BRANDING } from '../../../../constants/branding';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  BoxPlotController,
  BoxAndWiskers
);

const { COLORS } = PLATFORM_BRANDING;

/** Palette alignée charte FUNTOA eHealth+ */
export const CHART_PALETTE = [
  COLORS.PRIMARY,
  COLORS.SKY,
  COLORS.ORANGE,
  COLORS.PRIMARY_DARK,
  COLORS.GREEN,
  COLORS.YELLOW,
  COLORS.PRIMARY_BRIGHT,
  '#64748b',
  '#ec4899',
  '#06b6d4'
];

export const STATUS_CHART_COLORS = {
  ACTIVE: COLORS.PRIMARY,
  FULFILLED: COLORS.GREEN,
  CANCELLED: '#94a3b8'
};

export const CONSULTATION_STATUS_COLORS = {
  PENDING: COLORS.ORANGE,
  COMPLETED: COLORS.GREEN,
  CANCELLED: '#94a3b8'
};

export const EXAM_STATUS_COLORS = {
  REQUESTED: COLORS.PRIMARY,
  COMPLETED: COLORS.GREEN,
  CANCELLED: '#94a3b8'
};

export const REST_DAY_STATUS_COLORS = {
  ISSUED: COLORS.GREEN,
  CANCELLED: '#94a3b8'
};

export const SERVICE_LINE_COLORS = {
  MEDICAL: COLORS.PRIMARY,
  DENTAL: COLORS.PRIMARY_DARK,
  NURSING: COLORS.SKY
};

const GRID_COLOR = 'rgba(15, 23, 42, 0.08)';
const TICK_COLOR = '#64748b';

export function formatChartNumber(value) {
  return Number(value || 0).toLocaleString('fr-FR');
}

export function formatChartPercent(value, digits = 1) {
  return `${Number(value || 0).toFixed(digits).replace('.', ',')} %`;
}

export function baseCartesianOptions({ horizontal = false, yLabel, xLabel } = {}) {
  const valueAxisKey = horizontal ? 'x' : 'y';
  const categoryAxisKey = horizontal ? 'y' : 'x';

  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          usePointStyle: true,
          boxWidth: 8,
          padding: 16,
          color: TICK_COLOR,
          font: { size: 12, weight: '600' }
        }
      },
      tooltip: {
        backgroundColor: '#0f172a',
        titleColor: '#f8fafc',
        bodyColor: '#e2e8f0',
        padding: 12,
        cornerRadius: 8,
        callbacks: {
          label: (ctx) => {
            const label = ctx.dataset.label ? `${ctx.dataset.label} : ` : '';
            return `${label}${formatChartNumber(ctx.parsed[horizontal ? 'x' : 'y'])}`;
          }
        }
      }
    },
    scales: {
      [categoryAxisKey]: {
        grid: { display: false },
        ticks: {
          color: TICK_COLOR,
          font: { size: 11 },
          maxRotation: horizontal ? 0 : 45,
          minRotation: 0
        },
        title: xLabel && !horizontal
          ? { display: true, text: xLabel, color: TICK_COLOR, font: { size: 11, weight: '600' } }
          : yLabel && horizontal
            ? { display: true, text: yLabel, color: TICK_COLOR, font: { size: 11, weight: '600' } }
            : undefined
      },
      [valueAxisKey]: {
        beginAtZero: true,
        grid: { color: GRID_COLOR, drawBorder: false },
        ticks: {
          color: TICK_COLOR,
          font: { size: 11 },
          callback: (v) => formatChartNumber(v)
        },
        title: yLabel && !horizontal
          ? { display: true, text: yLabel, color: TICK_COLOR, font: { size: 11, weight: '600' } }
          : xLabel && horizontal
            ? { display: true, text: xLabel, color: TICK_COLOR, font: { size: 11, weight: '600' } }
            : undefined
      }
    }
  };
}

/** Courbe à double axe Y (ex. certificats vs jours de repos). */
export function dualAxisLineOptions({ yLabel, y1Label, xLabel } = {}) {
  const base = baseCartesianOptions({ yLabel, xLabel });
  return {
    ...base,
    scales: {
      x: base.scales.x,
      y: {
        ...base.scales.y,
        position: 'left'
      },
      y1: {
        beginAtZero: true,
        position: 'right',
        grid: { drawOnChartArea: false },
        ticks: {
          color: TICK_COLOR,
          font: { size: 11 },
          callback: (v) => formatChartNumber(v)
        },
        title: y1Label
          ? { display: true, text: y1Label, color: TICK_COLOR, font: { size: 11, weight: '600' } }
          : undefined
      }
    }
  };
}

export function baseArcOptions({ cutout = '58%' } = {}) {
  return {
    responsive: true,
    maintainAspectRatio: false,
    cutout,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          usePointStyle: true,
          boxWidth: 8,
          padding: 14,
          color: TICK_COLOR,
          font: { size: 11, weight: '600' }
        }
      },
      tooltip: {
        backgroundColor: '#0f172a',
        padding: 12,
        cornerRadius: 8,
        callbacks: {
          label: (ctx) => {
            const total = ctx.dataset.data.reduce((s, n) => s + Number(n), 0);
            const val = Number(ctx.parsed);
            const pct = total > 0 ? (val / total) * 100 : 0;
            return `${ctx.label} : ${formatChartNumber(val)} (${formatChartPercent(pct)})`;
          }
        }
      }
    }
  };
}
